import { useCallback, useMemo } from 'react';

import { useLocation } from 'react-router-dom';

import { IBaseItem, ICartEntry, ISanityItem } from '@rbi-ctg/menu';
import {
  IProductDetailInput,
  RecommendationType,
  ServiceMode,
  useListRecommendationItemsQuery,
} from 'generated/rbi-graphql';
import GetAllItemsQuery from 'remote/queries/all-items';
import { useSanityQuery } from 'state/graphql';
import { useFlag } from 'state/launchdarkly';
import { useMenuContext } from 'state/menu';
import { useMenuUIContext } from 'state/menu-ui';
import { useOffersContext } from 'state/offers';
import { useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';
import { CartEntryType } from 'utils/cart';
import { ComboSlotUiPattern } from 'utils/cart/types';
import { LaunchDarklyFlag } from 'utils/launchdarkly';
import { routes } from 'utils/routing';

import { IUseUpsell } from './types';

export const getProducts = (entries: ICartEntry[], multiplier = 1) => {
  let products: IProductDetailInput[] = [];
  entries.forEach((entry: ICartEntry) => {
    if (entry.uiPattern === ComboSlotUiPattern.HIDDEN) {
      return;
    } else if (entry.type === CartEntryType.item) {
      products.push({
        id: entry._id,
        quantity: entry.quantity * multiplier,
      });
    } else if (entry.children.length) {
      if (entry.type === CartEntryType.comboSlot) {
        products = products.concat(getProducts(entry.children));
      } else {
        products = products.concat({
          id: entry._id,
          quantity: entry.quantity * multiplier,
          sublevelItems: getProducts(entry.children),
        });
      }
    }
  });
  return products;
};

const useDeepflameUpsell = () => {
  const { cartEntries, serviceMode } = useOrderContext();
  const { filterForAvailability } = useMenuContext();
  const { activeCartEntries } = useMenuUIContext();
  const { selectedOfferCartEntry } = useOffersContext();
  const { store } = useStoreContext();
  const location = useLocation();
  const deprecateChef = useFlag(LaunchDarklyFlag.DEPRECATE_CHEF);
  const isInMenu = useCallback(() => location.pathname.includes(routes.menu), [location]);
  const isInCart = useCallback(() => location.pathname.includes(routes.cart), [location]);

  // This logic selects the cart entries that we send to the useUpsellRecommendationsQuery below
  // For this to work in menu, we send activeCartEntries - the item the user is viewing in the shape of a cart item
  // and when we navigate to the cart, we only send what the user has in their cart - cartEntries
  const allCartEntriesAndActiveItem = useMemo(() => {
    const offerMenuCartEntry = selectedOfferCartEntry ? [selectedOfferCartEntry] : [];
    return isInMenu()
      ? cartEntries.concat(offerMenuCartEntry, activeCartEntries)
      : cartEntries.concat(offerMenuCartEntry);
  }, [activeCartEntries, cartEntries, isInMenu, selectedOfferCartEntry]);

  const storeId: string = store.number ?? '';
  const productsTransformed = getProducts(allCartEntriesAndActiveItem);

  // Check URL for menu or cart - this works for intl sites as well with different languages
  // As we add more areas for upsell, we can replace "OTHER" with new locations
  const recommendationTypeLogic = () => {
    if (isInCart()) {
      return RecommendationType.CART;
    }
    return isInMenu() ? RecommendationType.MENU : RecommendationType.CART;
  };

  const { loading: upsellLoading, data: upsellData } = useListRecommendationItemsQuery({
    variables: {
      type: recommendationTypeLogic(),
      serviceMode: ServiceMode[serviceMode],
      productDetails: productsTransformed,
      storeId,
    },
    skip: !allCartEntriesAndActiveItem.length || !storeId || !deprecateChef,
  });

  const { loading: sanityLoading, data: sanityData } = useSanityQuery(GetAllItemsQuery, {
    variables: {
      where: {
        _id_in: upsellData?.listRecommendationItems?.results?.map(item => item?.id) || [],
      },
    },
    skip: !upsellData,
  });

  const items: IBaseItem[] = useMemo(
    () =>
      (upsellData?.listRecommendationItems?.results || [])
        .map(upsellItem =>
          sanityData?.allItems.find((item: IBaseItem) => item._id === upsellItem?.id)
        )
        .filter(
          (item: IBaseItem) =>
            // Check if exists
            item &&
            // Check if is available
            !!filterForAvailability(item as ISanityItem)
        ),
    [upsellData, sanityData, filterForAvailability]
  );

  return {
    items,
    loading: upsellLoading || sanityLoading,
    recommender: upsellData?.listRecommendationItems?.recommender,
    recommendationToken: upsellData?.listRecommendationItems?.recommendationToken || undefined,
    upsellData: upsellData?.listRecommendationItems?.results || undefined,
  } as IUseUpsell;
};

export default useDeepflameUpsell;
